import classNames from "classnames";
import React from "react";

export const badgeStyles = [
  "info",
  "default",
  "error",
  "error-dark",
  "warning",
  "success",
  "secondary",
  "primary-alt",
] as const;

type BadgeStyle = typeof badgeStyles[number];

type Props<T extends React.ElementType = "span" | "button"> = {
  as?: T;
  text: string;
  textStyle?: "light" | "dark";
  badgeStyle?: BadgeStyle;
  size?: "normal" | "small";
  textSize?: "xs" | "sm" | "md";
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  spacing?: "tight-hug";
  onRightIconClick?(): void;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

const Badge = ({
  as = "span",
  text,
  badgeStyle = "default",
  size = "normal",
  leftIcon,
  rightIcon,
  onRightIconClick,
  textStyle = "light",
  textSize = "md",
  onClick,
  disabled = false,
  spacing,
}: Props) => {
  const iconClassName = classNames(
    "rounded-3xl",
    { "text-lg": size === "normal" },
    { "text-base": size === "small" },
    {
      "bg-primary-bold group-hover:bg-primary-bold-700 group-active:bg-primary-bold-300 group-active:text-primary-bold":
        badgeStyle === "info",
      "bg-gray group-hover:bg-gray-700 group-active:bg-gray-300 group-active:text-gray":
        badgeStyle === "default",
      "bg-secondary-salmon group-hover:bg-secondary-salmon-700 group-active:bg-secondary-salmon-300 group-active:text-secondary-salmon":
        badgeStyle === "secondary",
      "bg-error group-hover:bg-error-700 group-active:bg-error-300 group-active:text-error":
        badgeStyle === "error",
      "bg-warning group-hover:bg-warning-700 group-active:bg-warning-300 group-active:text-warning":
        badgeStyle === "warning",
      "bg-tertiary-apple-600 group-hover:bg-tertiary-apple-800 group-active:bg-tertiary-apple-400 group-active:text-tertiary-apple-600":
        badgeStyle === "success",
      "bg-primary-light-800 group-hover:bg-primary-light-900 group-active:bg-primary-light group-active:text-primary-light-700":
        badgeStyle === "primary-alt",
    }
  );

  const Component = as;

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
      return;
    }
    if (onRightIconClick) {
      onRightIconClick();
    }
  };

  return (
    <Component
      type={as === "button" ? "button" : undefined}
      onClick={handleOnClick}
      className={classNames(
        "rounded-full inline-flex text-text-dark-100 items-center space-x-1 focus:outline-none focus-visible:shadow-elevation-01",
        {
          group: as === "button",
          "caption-1 px-2 py-1": size === "normal",
          "caption-3 px-1.5 py-0.5": size === "small",
          "bg-primary-bold": badgeStyle === "info",
          "hover:bg-primary-bold-300 active:bg-primary-bold":
            as === "button" && badgeStyle === "info",
          "bg-gray-600": badgeStyle === "default",
          "hover:bg-gray-300 active:bg-gray":
            as === "button" && badgeStyle === "default",
          "bg-secondary-salmon-100": badgeStyle === "secondary",
          "hover:bg-secondary-salmon-300 active:bg-secondary-salmon":
            as === "button" && badgeStyle === "secondary",
          "bg-error-100": badgeStyle === "error",
          "bg-secondary-salmon": badgeStyle === "error-dark",
          "hover:bg-error-300 active:bg-error":
            as === "button" && badgeStyle === "error",
          "bg-warning-100": badgeStyle === "warning",
          "hover:bg-warning-300 active:bg-warning":
            as === "button" && badgeStyle === "warning",
          "bg-tertiary-apple-200": badgeStyle === "success",
          "hover:bg-tertiary-apple-400 active:bg-tertiary-apple-600":
            as === "button" && badgeStyle === "success",
          "bg-primary-light": badgeStyle === "primary-alt",
          "hover:bg-primary-light-600 active:bg-primary-light-900":
            as === "button" && badgeStyle === "primary-alt",
        }
      )}
    >
      {!disabled && leftIcon && (
        <span className={iconClassName}>{leftIcon}</span>
      )}
      <span
        className={classNames("text-center whitespace-nowrap", {
          "text-text-dark-100": textStyle === "light",
          "text-gray-900": textStyle === "dark",
          "body-3": textSize === "md",
          "caption-1": textSize === "sm",
          "caption-4": textSize === "xs",
          "px-2": spacing === "tight-hug",
          "px-2 py-1": !spacing,
        })}
      >
        {text}
      </span>
      {!disabled && rightIcon && (
        <div className={iconClassName}>{rightIcon}</div>
      )}
    </Component>
  );
};

export default Badge;
